import { Controller } from "@hotwired/stimulus"

// The classic show-on-scroll-up header.
//
// Scrolling down is a signal that somebody wants the page, not the menu, so
// the header slides out of the way. Scrolling up is a signal they want the
// menu back, and it returns at once — no scrolling all the way to the top to
// reach the navigation, which on the calendar and the front page is a long way
// for an audience of 55+.
//
// It never hides:
//   - near the top of the page, where there is nothing to gain
//   - while the mobile drawer is open, which would take the open menu with it
//   - while focus is inside it, or a keyboard user would tab into a header
//     that has just left the screen (WCAG 2.4.11)
//   - for somebody who asked for reduced motion; the header simply stays put
export default class extends Controller {
  static values = {
    // Far enough down that the first flick of a finger does not take the
    // header with it.
    revealAt: { type: Number, default: 120 },
    // Small movements are noise — a trackpad wobble, a tap that nudges the
    // page. Only a deliberate stroke counts as intent.
    intent:   { type: Number, default: 8 }
  }

  connect() {
    this.lastY = window.scrollY
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.ticking = false

    this.onScroll = this.schedule.bind(this)
    this.onFocusIn = this.show.bind(this)

    window.addEventListener("scroll", this.onScroll, { passive: true })
    this.element.addEventListener("focusin", this.onFocusIn)
    this.update()
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    this.element.removeEventListener("focusin", this.onFocusIn)
  }

  schedule() {
    if (this.ticking) return

    this.ticking = true
    window.requestAnimationFrame(() => {
      this.update()
      this.ticking = false
    })
  }

  update() {
    const y = Math.max(window.scrollY, 0)
    const moved = y - this.lastY

    // A shadow only once the page has moved under it, so the header sits flat
    // on the paper until it is actually overlapping something.
    this.element.classList.toggle("shadow-sm", y > 4)

    if (Math.abs(moved) < this.intentValue) return
    this.lastY = y

    if (this.reducedMotion || this.drawerOpen || y <= this.revealAtValue) return this.show()

    moved > 0 ? this.hide() : this.show()
  }

  get drawerOpen() {
    return this.element.querySelector('[aria-expanded="true"]') !== null
  }

  hide() {
    this.element.classList.add("-translate-y-full")
  }

  show() {
    this.element.classList.remove("-translate-y-full")
  }
}
